import {
  Avatar,
  Button,
  Divider,
  Dropdown,
  Form,
  Grid,
  Input,
  Layout,
  Modal,
  Popover,
  Space,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  FileTextOutlined,
  CodeOutlined,
  LockOutlined,
  LogoutOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  MenuOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDesignSystem } from '../../../app/providers/ThemeProvider';
import { useKeyboardMode } from '../../../app/providers/KeyboardModeProvider';
import { useNavigationAids } from '../../../app/providers/NavigationAidsProvider';
import { useGuardedNavigate } from '../../../app/providers/FormSafetyProvider';
import { useAuthSession } from '../../../app/providers/AuthSessionProvider';
import { useClerkRuntime } from '../../../app/providers/ClerkRuntimeProvider';
import { useWorkspaceShowcase } from '../../../app/providers/WorkspaceShowcaseProvider';
import { API_BASE_URL, ENABLE_AUTH } from '../../utils/constants';
import { clearAdminSession } from '../../api/adminSessionStore';
import { hasPermission } from '../../auth/permissions';
import { getFlowMeta } from './flowMeta';
import { mapKeyboardToStandardPath, mapStandardToKeyboardPath } from './keyboardModeRouting';
import { isCookieAuthToken } from '../../api/browserSession';
import styles from './Topbar.module.css';

const { Header } = Layout;
const { Text, Title } = Typography;
const { useBreakpoint } = Grid;
const KEYBOARD_SHORTCUTS = [
  { key: 'Up/Down', label: 'Move' },
  { key: 'Enter', label: 'Select' },
  { key: 'Esc', label: 'Back' },
];
const QUICK_CREATE_ITEMS = Object.freeze([
  { key: '/invoices/new', label: 'Invoice' },
  { key: '/quotations/new', label: 'Quotation' },
  { key: '/estimated-invoices/new', label: 'Estimate' },
]);

export default function Topbar({
  showNavigationToggle = false,
  onOpenNavigation = null,
  navigationOpen = false,
  navigationDrawerId = 'app-navigation-drawer',
  navigationToggleRef = null,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const guardedNavigate = useGuardedNavigate();
  const { getLastStandardPath, setLastStandardPath } = useKeyboardMode();
  const { activeNavigationLayout, setUiCustomization, showHelperText, uiCustomization } = useDesignSystem();
  const { session, clearSession } = useAuthSession();
  const clerkRuntime = useClerkRuntime();
  const [loggingOut, setLoggingOut] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [changePasswordForm] = Form.useForm();
  const {
    breadcrumbs,
    isActiveFavorite,
    openCommandPalette,
    toggleFavorite,
  } = useNavigationAids();
  const { startReplay, isReplayAvailable } = useWorkspaceShowcase();

  const inKeyboardMode = location.pathname.startsWith('/keyboard');
  const flowMeta = getFlowMeta(location.pathname);
  const layoutId = activeNavigationLayout.id;
  const userLabel = session?.user?.fullName || 'Workspace User';
  const roleLabel = session?.roleLabel || session?.role || 'viewer';
  const sessionPermissions = Array.isArray(session?.permissions) ? session.permissions : [];
  const canUseKeyboardMode = hasPermission(sessionPermissions, 'keyboardMode:use');
  const isMobile = screens.md === false;
  const quickCreateItems = QUICK_CREATE_ITEMS.filter((item) => {
    if (item.key === '/invoices/new') {
      return hasPermission(sessionPermissions, 'invoices:write');
    }
    if (item.key === '/quotations/new') {
      return hasPermission(sessionPermissions, 'quotations:write');
    }
    if (item.key === '/estimated-invoices/new') {
      return hasPermission(sessionPermissions, 'estimatedInvoices:write');
    }
    return false;
  });
  const userInitial = (session?.user?.fullName || session?.user?.email || 'U')
    .trim()
    .charAt(0)
    .toUpperCase();
  const isClerkWorkspaceSession =
    clerkRuntime.workspaceEnabled && session?.authSource === 'clerk';

  useEffect(() => {
    if (!inKeyboardMode) {
      setLastStandardPath(location.pathname);
    }
  }, [inKeyboardMode, location.pathname, setLastStandardPath]);

  const toggleKeyboardMode = (enabled) => {
    if (enabled) {
      guardedNavigate(mapStandardToKeyboardPath(location.pathname));
    } else {
      const fallback = getLastStandardPath() || '/dashboard';
      guardedNavigate(mapKeyboardToStandardPath(location.pathname, fallback));
    }
  };

  const handleQuickCreate = ({ key }) => {
    if (!key) {
      return;
    }

    guardedNavigate(inKeyboardMode ? mapStandardToKeyboardPath(key) : key);
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      if (!isClerkWorkspaceSession) {
        await fetch(`${API_BASE_URL}/auth/web/logout`, {
          method: 'POST',
          credentials: ENABLE_AUTH ? 'include' : 'omit',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });
      }
    } catch (_error) {
      // Always clear local session, even if backend cookie clear fails.
    } finally {
      clearSession();
      clearAdminSession();
      setLoggingOut(false);
      if (isClerkWorkspaceSession) {
        try {
          await clerkRuntime.signOut({ redirectUrl: '/login' });
          return;
        } catch (_error) {
          // Fall back to app navigation below.
        }
      }
      navigate('/login', { replace: true });
    }
  };

  const closeChangePasswordModal = () => {
    setChangePasswordOpen(false);
    setChangePasswordError('');
    changePasswordForm.resetFields();
  };

  const openChangePasswordModal = () => {
    setAccountMenuOpen(false);
    setChangePasswordOpen(true);
  };

  const handleChangePassword = async (values) => {
    setChangingPassword(true);
    setChangePasswordError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        credentials: ENABLE_AUTH ? 'include' : 'omit',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.accessToken && !isCookieAuthToken(session.accessToken)
            ? {
                Authorization: `Bearer ${session.accessToken}`,
              }
            : {}),
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        let payload = null;
        try {
          payload = await response.json();
        } catch (_error) {
          payload = null;
        }

        const errorMessage =
          payload?.error?.message ||
          payload?.message ||
          'Unable to update password.';
        throw new Error(errorMessage);
      }

      message.success('Password updated. Please sign in again.');
      closeChangePasswordModal();
      clearSession();
      navigate('/login', { replace: true });
    } catch (error) {
      setChangePasswordError(error?.message || 'Unable to update password.');
    } finally {
      setChangingPassword(false);
    }
  };

  const accountMenu = (
    <div className={styles.accountMenu} data-testid="topbar-account-menu">
      <div className={styles.accountMenuHeader}>
        <Avatar className={styles.accountMenuAvatar} size={44}>
          {userInitial}
        </Avatar>
        <div className={styles.accountMenuMeta}>
          <strong>{userLabel}</strong>
          <Text className={styles.accountMenuRole}>{roleLabel}</Text>
        </div>
      </div>
      <Divider className={styles.accountMenuDivider} />
      <div className={styles.accountMenuActions}>
        {!inKeyboardMode ? (
          <Button
            type="text"
            className={styles.accountAction}
            icon={isActiveFavorite ? <StarFilled /> : <StarOutlined />}
            onClick={() => {
              setAccountMenuOpen(false);
              toggleFavorite();
            }}
          >
            {isActiveFavorite ? 'Remove favorite' : 'Add to favorites'}
          </Button>
        ) : null}
        {!isMobile && (inKeyboardMode || canUseKeyboardMode) ? (
          <Button
            type="text"
            className={styles.accountAction}
            icon={<CodeOutlined />}
            onClick={() => {
              setAccountMenuOpen(false);
              toggleKeyboardMode(!inKeyboardMode);
            }}
          >
            {inKeyboardMode ? 'Return to standard mode' : 'Open keyboard mode'}
          </Button>
        ) : null}
        {!isClerkWorkspaceSession ? (
          <Button
            type="text"
            className={styles.accountAction}
            icon={<LockOutlined />}
            onClick={openChangePasswordModal}
            data-testid="topbar-account-password-action"
          >
            Change password
          </Button>
        ) : null}
        <Button
          type="text"
          danger
          className={styles.accountAction}
          icon={<LogoutOutlined />}
          loading={loggingOut}
          onClick={() => {
            setAccountMenuOpen(false);
            handleLogout();
          }}
        >
          Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <Header className={styles.header}>
      <div
        className={[
          styles.surface,
          styles[`surface${layoutId}`],
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid="topbar-surface"
        data-navigation-layout={layoutId}
      >
        <div className={styles.leading}>
          {showNavigationToggle ? (
            <Button
              ref={navigationToggleRef}
              className={styles.navToggle}
              icon={<MenuOutlined />}
              onClick={onOpenNavigation}
              aria-label="Open navigation"
              aria-haspopup="dialog"
              aria-controls={navigationDrawerId}
              aria-expanded={navigationOpen}
              data-testid="topbar-nav-toggle"
              data-showcase-id="topbar-nav-toggle"
            />
          ) : null}
          <div className={styles.titleBlock}>
            {uiCustomization.showTopbarContext ? (
              <Text className={styles.kicker}>{flowMeta.section}</Text>
            ) : null}
            {!inKeyboardMode && uiCustomization.showTopbarContext && breadcrumbs.length ? (
              <nav className={styles.breadcrumbs} aria-label="Breadcrumbs">
                {breadcrumbs.map((item, index) => (
                  <span key={item.key} className={styles.breadcrumbItem}>
                    {item.path && index < breadcrumbs.length - 1 ? (
                      <button
                        type="button"
                        className={styles.breadcrumbButton}
                        onClick={() => guardedNavigate(item.path)}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <span className={styles.breadcrumbCurrent}>{item.label}</span>
                    )}
                    {index < breadcrumbs.length - 1 ? (
                      <span className={styles.breadcrumbDivider}>/</span>
                    ) : null}
                  </span>
                ))}
              </nav>
            ) : null}
            <Title level={4} className={styles.title}>
              {flowMeta.title}
            </Title>
          </div>
        </div>

        <div className={styles.controls}>
          {inKeyboardMode ? (
            <div className={styles.keyboardLegend} aria-label="Keyboard shortcuts">
              {KEYBOARD_SHORTCUTS.map((item) => (
                <span key={item.key} className={styles.keyboardLegendItem}>
                  <span className={styles.keycap}>{item.key}</span>
                  <span>{item.label}</span>
                </span>
              ))}
            </div>
          ) : null}
          <Space className={styles.actionRow}>
            {!isMobile ? (
              <Tooltip title="Replay the workspace guide">
                <Button
                  className={styles.toolButton}
                  icon={<QuestionCircleOutlined />}
                  onClick={startReplay}
                  disabled={!isReplayAvailable}
                  data-testid="topbar-showcase-trigger"
                  data-showcase-id="topbar-guide"
                >
                  Guide
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Replay the workspace guide">
                <Button
                  className={styles.toolButton}
                  icon={<QuestionCircleOutlined />}
                  onClick={startReplay}
                  disabled={!isReplayAvailable}
                  aria-label="Replay workspace guide"
                  data-testid="topbar-showcase-trigger"
                  data-showcase-id="topbar-guide"
                />
              </Tooltip>
            )}
            {!isMobile ? (
              <Tooltip title={showHelperText ? 'Hide extra descriptions' : 'Show extra descriptions'}>
                <Button
                  className={`${styles.toolButton} ${showHelperText ? styles.toolButtonActive : ''}`}
                  icon={<FileTextOutlined />}
                  onClick={() =>
                    setUiCustomization((currentValue) => ({
                      showHelperText: !currentValue.showHelperText,
                    }))
                  }
                  data-testid="topbar-helper-text-toggle"
                >
                  Details
                </Button>
              </Tooltip>
            ) : null}
            {!inKeyboardMode && !isMobile ? (
              <Tooltip title="Search pages and actions (Ctrl/Cmd + K)">
                <Button
                  className={styles.toolButton}
                  icon={<SearchOutlined />}
                  onClick={openCommandPalette}
                  data-testid="command-palette-toggle"
                  data-showcase-id="topbar-command"
                >
                  Command
                </Button>
              </Tooltip>
            ) : null}
            {inKeyboardMode ? <Text className={styles.modeTag}>Keyboard</Text> : null}
            {quickCreateItems.length ? (
              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                menu={{
                  items: quickCreateItems,
                  onClick: handleQuickCreate,
                }}
              >
                <Button
                  className={styles.toolButton}
                  icon={<PlusOutlined />}
                  data-testid="topbar-quick-create-trigger"
                  data-showcase-id="topbar-add"
                >
                  Add
                </Button>
              </Dropdown>
            ) : null}
            <Popover
              trigger="click"
              placement="bottomRight"
              open={accountMenuOpen}
              onOpenChange={setAccountMenuOpen}
              content={accountMenu}
            >
              <button
                type="button"
                className={styles.accountTrigger}
                aria-label="Open account menu"
                data-testid="topbar-account-trigger"
                data-showcase-id="topbar-account"
              >
                <Avatar className={styles.accountAvatar} size={42}>
                  {userInitial}
                </Avatar>
              </button>
            </Popover>
          </Space>
        </div>
      </div>
      <Modal
        title="Change password"
        open={changePasswordOpen}
        onCancel={closeChangePasswordModal}
        onOk={() => changePasswordForm.submit()}
        okText="Update password"
        confirmLoading={changingPassword}
        destroyOnHidden
      >
        <Form
          form={changePasswordForm}
          layout="vertical"
          onFinish={handleChangePassword}
          autoComplete="off"
        >
          <Form.Item
            name="currentPassword"
            label="Current password"
            rules={[{ required: true, message: 'Current password is required.' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New password"
            rules={[
              { required: true, message: 'New password is required.' },
              { min: 8, message: 'New password must be at least 8 characters.' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmNewPassword"
            label="Confirm new password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Confirm your new password.' },
              ({ getFieldValue }) => ({
                validator(_rule, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Password confirmation does not match.'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          {changePasswordError ? <Text type="danger">{changePasswordError}</Text> : null}
        </Form>
      </Modal>
    </Header>
  );
}
